import React, { useState, useEffect, useCallback, useMemo } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  TableSortLabel,
  Link
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Settings,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Delete,
  Edit,
  Add,
  Person,
  Block,
  Star,
  History,
  Assessment,
  Search,
  Visibility,
  PersonAdd,
  Analytics,
  OpenInNew,
  Close,
  AutoMode,
  ThumbUp,
  ThumbDown,
  PlayArrow as ScanIcon,
  AccountTree,
  Download,
  Shuffle,
  VpnKey
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
  ReferenceDot
} from 'recharts';
import { adminAPI } from '../services/api';
import { formatNumber, formatCurrency, formatClean, formatPrice } from '../utils/formatters';

// Вспомогательная функция для форматирования адреса
const formatAddress = (address) => {
  if (!address) return '-';
  return `${address.substring(0, 8)}...${address.slice(-6)}`;
};

// Форматирование числа без лишних нулей (30.00 → 30)
const formatSettingNum = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = parseFloat(value);
  return isNaN(num) ? fallback : num;
};

// Русификация типов событий MM
const formatEventType = (eventType) => {
  const eventTypes = {
    'mm_started': '🚀 Запуск',
    'mm_stopped': '🛑 Остановка',
    'trade_executed': '✅ Сделка',
    'trade_failed': '❌ Ошибка сделки',
    'trade_error': '⚠️ Ошибка',
    'flipper_detected': '🎯 Флиппер',
    'circuit_breaker': '🔌 Circuit Breaker',
    'price_boundary': '📊 Граница цены',
    'low_balance': '💰 Низкий баланс'
  };
  return eventTypes[eventType] || eventType;
};

// Русификация типов сделок
const formatTradeType = (message) => {
  if (!message) return message;
  return message
    .replace('counter_sell', 'контр-продажа')
    .replace('ceiling_sell', 'продажа (потолок)')
    .replace('floor_buy', 'покупка (пол)')
    .replace('Сделка', 'Сделка:');
};

// Вспомогательная функция для форматирования даты
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('ru-RU');
};

// Компонент вкладки
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

const MarketMaker = () => {
  const [tabIndex, setTabIndex] = usePersistedTab('marketmaker', 0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Данные
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [walletRules, setWalletRules] = useState([]);
  const [trades, setTrades] = useState([]);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [price, setPrice] = useState(null);

  // Режим роста
  const [growthStatus, setGrowthStatus] = useState(null);
  const [growthRate, setGrowthRate] = useState(2);
  const [growthTargetPrice, setGrowthTargetPrice] = useState('');
  const [togglingGrowth, setTogglingGrowth] = useState(false);

  // Автообмен входящих TON на SPERM

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [walletDialog, setWalletDialog] = useState({ open: false, mode: 'add', data: null });

  // Scanner state
  const [scanResults, setScanResults] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanParams, setScanParams] = useState({ limit: 20, days: 30, minTrades: 3 });
  const [scanMeta, setScanMeta] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: 'ton_profit', direction: 'desc' });

  // Сортировка результатов сканирования
  const sortedScanResults = useMemo(() => {
    if (!scanResults.length) return [];

    const sorted = [...scanResults].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.field) {
        case 'total_trades':
          aVal = a.total_trades || 0;
          bVal = b.total_trades || 0;
          break;
        case 'ton_profit':
          aVal = a.ton_profit || 0;
          bVal = b.ton_profit || 0;
          break;
        case 'profit_percent':
          aVal = a.profit_percent || 0;
          bVal = b.profit_percent || 0;
          break;
        case 'is_likely_flipper':
          aVal = a.is_likely_flipper ? 1 : 0;
          bVal = b.is_likely_flipper ? 1 : 0;
          break;
        case 'last_trade':
          aVal = a.last_trade || 0;
          bVal = b.last_trade || 0;
          break;
        case 'project_link':
          // Сортировка по связи: сначала те у кого есть связь с проектом
          aVal = a.project_link?.is_project_user ? 1 : 0;
          bVal = b.project_link?.is_project_user ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (sortConfig.direction === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });

    return sorted;
  }, [scanResults, sortConfig]);

  // Обработчик сортировки
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletDetails, setWalletDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Flipper Scanner state
  const [flipperCandidates, setFlipperCandidates] = useState([]);
  const [flipperStats, setFlipperStats] = useState(null);
  const [flipperScannerStats, setFlipperScannerStats] = useState(null);
  const [loadingFlipperScan, setLoadingFlipperScan] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null); // Результат последнего сканирования
  const [flipperStatusFilter, setFlipperStatusFilter] = useState('pending'); // Фильтр: pending, all, approved, rejected

  // Distribution state
  const [distStatus, setDistStatus] = useState(null);
  const [distWallets, setDistWallets] = useState([]);
  const [distWalletsTotal, setDistWalletsTotal] = useState(0);
  const [distLogs, setDistLogs] = useState([]);
  const [distLogsTotal, setDistLogsTotal] = useState(0);
  const [distTierFilter, setDistTierFilter] = useState('');
  const [distWalletPage, setDistWalletPage] = useState(1);
  const [distLogPage, setDistLogPage] = useState(1);
  const [distLoading, setDistLoading] = useState(false);
  const [distExportDialog, setDistExportDialog] = useState(false);
  const [distExportPassword, setDistExportPassword] = useState('');
  const [distExportLoading, setDistExportLoading] = useState(false);

  // Distribution: диалог отправки и sweep
  const [distSendDialog, setDistSendDialog] = useState(false);
  const [distSendData, setDistSendData] = useState({ walletId: null, toAddress: '', amount: '', currency: 'ton', comment: '' });
  const [distSendLoading, setDistSendLoading] = useState(false);
  const [distSweepDialog, setDistSweepDialog] = useState(false);
  const [distSweepTarget, setDistSweepTarget] = useState('');
  const [distSweepCurrencies, setDistSweepCurrencies] = useState(['ton']);
  const [distSweepLoading, setDistSweepLoading] = useState(false);
  const [sweepId, setSweepId] = useState(null);
  const [sweepProgress, setSweepProgress] = useState(null);
  const [distBalanceLoading, setDistBalanceLoading] = useState({});
  const [distOnchainBalances, setDistOnchainBalances] = useState({});
  const [distRefreshLoading, setDistRefreshLoading] = useState(false);
  const [distSort, setDistSort] = useState('default');
  const [distSortDir, setDistSortDir] = useState('asc');

  // Загрузка данных Distribution
  const loadDistributionData = useCallback(async () => {
    try {
      setDistLoading(true);
      const [statusRes, walletsRes, logsRes] = await Promise.all([
        adminAPI.getDistributionStatus(),
        adminAPI.getDistributionWallets({ tier: distTierFilter, page: distWalletPage, limit: 50, sort: distSort, sortDir: distSortDir }),
        adminAPI.getDistributionLogs({ page: distLogPage, limit: 30 })
      ]);
      if (statusRes.data.success) setDistStatus(statusRes.data.data);
      if (walletsRes.data.success) {
        setDistWallets(walletsRes.data.data.wallets);
        setDistWalletsTotal(walletsRes.data.data.total);
      }
      if (logsRes.data.success) {
        setDistLogs(logsRes.data.data.logs);
        setDistLogsTotal(logsRes.data.data.total);
      }
    } catch (err) {
      console.error('Distribution load error:', err);
    } finally {
      setDistLoading(false);
    }
  }, [distTierFilter, distWalletPage, distLogPage, distSort, distSortDir]);

  const handleDistStart = async () => {
    try {
      const res = await adminAPI.startDistribution();
      if (res.data.success) {
        setSuccess('Распределение запущено');
        loadDistributionData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка запуска');
    }
  };

  const handleDistStop = async () => {
    try {
      const res = await adminAPI.stopDistribution();
      if (res.data.success) {
        setSuccess('Распределение остановлено');
        loadDistributionData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка остановки');
    }
  };

  const handleDistActivity = async () => {
    try {
      const res = await adminAPI.triggerDistributionActivity();
      if (res.data.success) {
        setSuccess(`Рандомная активность запущена: ${res.data.data.scheduled} кошельков`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка запуска активности');
    }
  };

  const handleDistExport = async () => {
    try {
      setDistExportLoading(true);
      const res = await adminAPI.exportDistributionSeeds(distExportPassword);
      if (res.data.success) {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `distribution-seeds-${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setDistExportDialog(false);
        setDistExportPassword('');
        setSuccess('Seed-фразы экспортированы');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка экспорта');
    } finally {
      setDistExportLoading(false);
    }
  };

  // Distribution: отправка с кошелька
  const [distSendError, setDistSendError] = useState('');
  const [distSendSuccess, setDistSendSuccess] = useState('');
  const handleDistSend = async () => {
    try {
      setDistSendLoading(true);
      setDistSendError('');
      setDistSendSuccess('');
      const { walletId, toAddress, amount, currency, comment } = distSendData;
      const res = await adminAPI.distributionSend({ walletId, toAddress, amount: parseFloat(amount), currency, comment });
      if (res.data.success) {
        const d = res.data.data;
        const msg = `Отправлено ${d.amount} ${d.currency.toUpperCase()} (confirmed: ${d.confirmed})`;
        setDistSendSuccess(msg);
        setSuccess(msg);
        loadDistributionData();
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Ошибка отправки';
      setDistSendError(errMsg);
    } finally {
      setDistSendLoading(false);
    }
  };

  // Distribution: sweep с live-прогрессом
  const sweepIntervalRef = React.useRef(null);

  const handleDistSweep = async () => {
    try {
      setDistSweepLoading(true);
      setSweepProgress(null);
      const res = await adminAPI.distributionSweep({ targetAddress: distSweepTarget, currencies: distSweepCurrencies });
      if (res.data.success) {
        const id = res.data.data.sweepId;
        setSweepId(id);
        // Поллинг каждые 2.5 сек
        sweepIntervalRef.current = setInterval(async () => {
          try {
            const statusRes = await adminAPI.getSweepStatus(id);
            setSweepProgress(statusRes.data.data);
            if (['completed', 'aborted', 'error'].includes(statusRes.data.data.status)) {
              clearInterval(sweepIntervalRef.current);
              sweepIntervalRef.current = null;
              setDistSweepLoading(false);
              loadDistributionData();
            }
          } catch (e) {
            clearInterval(sweepIntervalRef.current);
            sweepIntervalRef.current = null;
            setDistSweepLoading(false);
          }
        }, 2500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка sweep');
      setDistSweepLoading(false);
    }
  };

  const handleSweepAbort = async () => {
    if (sweepId) {
      try {
        await adminAPI.abortSweep(sweepId);
      } catch (e) { /* ignore */ }
    }
  };

  // Cleanup интервала при размонтировании
  React.useEffect(() => {
    return () => { if (sweepIntervalRef.current) clearInterval(sweepIntervalRef.current); };
  }, []);

  // Distribution: проверить on-chain баланс кошелька
  const handleDistCheckBalance = async (walletId) => {
    try {
      setDistBalanceLoading(prev => ({ ...prev, [walletId]: true }));
      const res = await adminAPI.getDistributionWalletBalance(walletId);
      if (res.data.success) {
        setDistOnchainBalances(prev => ({ ...prev, [walletId]: res.data.data }));
      }
    } catch (err) {
      setError(`Ошибка баланса #${walletId}: ${err.response?.data?.error || err.message}`);
    } finally {
      setDistBalanceLoading(prev => ({ ...prev, [walletId]: false }));
    }
  };

  // Distribution: открыть диалог отправки
  const openDistSendDialog = (walletId, currency = 'ton') => {
    setDistSendData({ walletId, toAddress: '', amount: '', currency, comment: '' });
    setDistSendError('');
    setDistSendSuccess('');
    setDistSendDialog(true);
  };

  // Distribution: обновить ВСЕ балансы (on-chain → DB)
  const handleDistRefreshBalances = async () => {
    try {
      setDistRefreshLoading(true);
      setSuccess('');
      const res = await adminAPI.refreshDistributionBalances({ tier: distTierFilter || undefined });
      if (res.data.success) {
        const d = res.data.data;
        const s = d.summary || {};
        setSuccess(`Балансы обновлены (${d.updated}/${d.total}). Итого: ${s.total_ton || 0} TON на ${s.wallets_ton || 0} кош. | ${formatNumber(s.total_sperm || 0)} SPERM на ${s.wallets_sperm || 0} кош.`);
        setDistOnchainBalances({});
        loadDistributionData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка обновления балансов');
    } finally {
      setDistRefreshLoading(false);
    }
  };

  // Distribution: переключить сортировку
  const handleDistSortToggle = (column) => {
    if (distSort === column) {
      setDistSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setDistSort(column);
      setDistSortDir('desc');
    }
    setDistWalletPage(1);
  };

  // Загрузка данных
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [statusRes, settingsRes, statsRes, priceRes, growthRes] = await Promise.all([
        adminAPI.getMarketMakerStatus(),
        adminAPI.getMarketMakerSettings(),
        adminAPI.getMarketMakerStats({ period: '7d' }),
        adminAPI.getMarketMakerPrice(),
        adminAPI.getGrowthStatus()
      ]);

      if (statusRes.data.success) setStatus(statusRes.data.data);
      if (settingsRes.data.success) setSettings(settingsRes.data.data);
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (priceRes.data.success) setPrice(priceRes.data.data);
      if (growthRes.data.success) {
        setGrowthStatus(growthRes.data.data);
        if (growthRes.data.data.dailyGrowthPercent) {
          setGrowthRate(growthRes.data.data.dailyGrowthPercent);
        }
        if (growthRes.data.data.finalTargetPrice) {
          setGrowthTargetPrice(growthRes.data.data.finalTargetPrice);
        } else if (growthRes.data.data.growthTargetPrice) {
          setGrowthTargetPrice(growthRes.data.data.growthTargetPrice);
        }
      }

    } catch (err) {
      console.error('Ошибка загрузки MM:', err);
      setError('Ошибка загрузки данных Market Maker');
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка правил кошельков
  const loadWalletRules = useCallback(async () => {
    try {
      const res = await adminAPI.getMarketMakerWallets();
      if (res.data.success) {
        setWalletRules(res.data.data || []);
      }
    } catch (err) {
      console.error('Ошибка загрузки правил:', err);
    }
  }, []);

  // Загрузка истории сделок
  const loadTrades = useCallback(async () => {
    try {
      const res = await adminAPI.getMarketMakerTrades({ limit: 100 });
      if (res.data.success) {
        setTrades(res.data.data || []);
      }
    } catch (err) {
      console.error('Ошибка загрузки сделок:', err);
    }
  }, []);

  // Загрузка логов
  const loadLogs = useCallback(async () => {
    try {
      const res = await adminAPI.getMarketMakerLogs({ limit: 100 });
      if (res.data.success) {
        setLogs(res.data.data || []);
      }
    } catch (err) {
      console.error('Ошибка загрузки логов:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Загрузка кандидатов в флипперы
  const loadFlipperCandidates = useCallback(async () => {
    try {
      const res = await adminAPI.getFlipperCandidates();
      if (res.data.success) {
        setFlipperCandidates(res.data.data || []);
        setFlipperStats(res.data.stats || null);
        setFlipperScannerStats(res.data.scanner || null);
      }
    } catch (err) {
      console.error('Ошибка загрузки flipper candidates:', err);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 0) loadData(); // Вкладка роста — обновляем все данные
    if (tabIndex === 2) loadWalletRules();
    if (tabIndex === 3) loadTrades();
    if (tabIndex === 4) loadLogs();
    if (tabIndex === 6) loadFlipperCandidates();
    if (tabIndex === 7) loadDistributionData();
    // tabIndex === 5 - Ручной Сканер (загружается по требованию)
  }, [tabIndex, loadWalletRules, loadTrades, loadLogs, loadFlipperCandidates, loadData, loadDistributionData, distTierFilter, distWalletPage, distLogPage]);

  // Сканирование прибыльных кошельков
  const handleScan = async () => {
    try {
      setScanning(true);
      setError('');
      setScanResults([]);
      setSelectedWallet(null);
      setWalletDetails(null);

      const res = await adminAPI.scanProfitableWallets(scanParams);

      if (res.data.success) {
        setScanResults(res.data.data || []);
        setScanMeta(res.data.meta || null);
        setSuccess(`Найдено ${res.data.data?.length || 0} прибыльных кошельков`);
      } else {
        setError(res.data.error || 'Ошибка сканирования');
      }
    } catch (err) {
      console.error('Ошибка сканирования:', err);
      setError('Ошибка сканирования кошельков');
    } finally {
      setScanning(false);
    }
  };

  // Получить детализацию кошелька
  const handleViewWalletDetails = async (wallet) => {
    try {
      setLoadingDetails(true);
      setSelectedWallet(wallet.wallet_address);

      const res = await adminAPI.getWalletDetails(wallet.wallet_address, scanParams.days);

      if (res.data.success) {
        setWalletDetails(res.data.data);
      } else {
        setError(res.data.error || 'Ошибка получения детализации');
      }
    } catch (err) {
      console.error('Ошибка получения детализации:', err);
      setError('Ошибка получения детализации кошелька');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Добавить кошелёк как флиппер
  const handleAddAsFlipper = async (wallet) => {
    try {
      const res = await adminAPI.addFlipper({
        wallet_address: wallet.wallet_address,
        sell_percentage: 100,
        comment: `Флиппер: PNL ${formatClean(wallet.ton_profit, 4)} TON (${formatClean(wallet.profit_percent, 1)}%)`
      });

      if (res.data.success) {
        setSuccess(`Кошелёк добавлен как флиппер (100% гашение)`);
        // Обновляем список в результатах сканирования
        setScanResults(prev => prev.map(w =>
          w.wallet_address === wallet.wallet_address
            ? { ...w, added_as_flipper: true }
            : w
        ));
        loadWalletRules();
      } else {
        setError(res.data.error || 'Ошибка добавления');
      }
    } catch (err) {
      console.error('Ошибка добавления флиппера:', err);
      setError('Ошибка добавления кошелька как флиппера');
    }
  };

  // Запуск автоматического сканирования флипперов
  const handleAutoScan = async () => {
    try {
      setLoadingFlipperScan(true);
      setError('');
      setLastScanResult(null);
      const res = await adminAPI.runFlipperScan();
      if (res.data.success) {
        // Сохраняем полный результат сканирования
        setLastScanResult(res.data);
        // Показываем сообщение из бекенда
        if (res.data.found > 0) {
          setSuccess(res.data.message || `Найдено ${res.data.found} новых флипперов`);
        }
        loadFlipperCandidates();
      } else {
        setError(res.data.error || res.data.reason || 'Ошибка сканирования');
      }
    } catch (err) {
      console.error('Ошибка авто-сканирования:', err);
      setError('Ошибка запуска сканирования');
    } finally {
      setLoadingFlipperScan(false);
    }
  };

  // Фильтрация кандидатов по статусу
  const filteredCandidates = useMemo(() => {
    if (flipperStatusFilter === 'all') return flipperCandidates;
    return flipperCandidates.filter(c => c.status === flipperStatusFilter);
  }, [flipperCandidates, flipperStatusFilter]);

  // Одобрить кандидата
  const handleApproveCandidate = async (id) => {
    try {
      const res = await adminAPI.approveFlipperCandidate(id);
      if (res.data.success) {
        setSuccess('Кандидат добавлен в флипперы');
        loadFlipperCandidates();
        loadWalletRules();
      } else {
        setError(res.data.error || 'Ошибка одобрения');
      }
    } catch (err) {
      console.error('Ошибка одобрения:', err);
      setError('Ошибка одобрения кандидата');
    }
  };

  // Отклонить кандидата
  const handleRejectCandidate = async (id) => {
    try {
      const res = await adminAPI.rejectFlipperCandidate(id);
      if (res.data.success) {
        setSuccess('Кандидат отклонён');
        loadFlipperCandidates();
      } else {
        setError(res.data.error || 'Ошибка отклонения');
      }
    } catch (err) {
      console.error('Ошибка отклонения:', err);
      setError('Ошибка отклонения кандидата');
    }
  };

  // Удалить кандидата
  const handleDeleteCandidate = async (id) => {
    if (!window.confirm('Удалить кандидата?')) return;
    try {
      const res = await adminAPI.deleteFlipperCandidate(id);
      if (res.data.success) {
        setSuccess('Кандидат удалён');
        loadFlipperCandidates();
      } else {
        setError(res.data.error || 'Ошибка удаления');
      }
    } catch (err) {
      console.error('Ошибка удаления:', err);
      setError('Ошибка удаления кандидата');
    }
  };

  // Форматирование timestamp в читаемую дату
  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts * 1000);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  // Вычисление "давности" последнего свопа
  const getTimeSince = (ts) => {
    if (!ts) return '-';
    const now = Math.floor(Date.now() / 1000);
    const diff = now - ts;
    if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} дн назад`;
    return `${Math.floor(diff / 604800)} нед назад`;
  };

  // Включить/выключить MM
  const handleToggle = async () => {
    try {
      setSaving(true);
      const newState = !status?.isRunning;
      await adminAPI.toggleMarketMaker({ enabled: newState });
      setSuccess(newState ? 'Market Maker запущен' : 'Market Maker остановлен');
      loadData();
    } catch (err) {
      setError('Ошибка переключения Market Maker');
    } finally {
      setSaving(false);
    }
  };

  // Сохранение настроек
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await adminAPI.saveMarketMakerSettings(settings);
      setSuccess('Настройки сохранены');
      loadData();
    } catch (err) {
      setError('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  // Включить/выключить режим роста
  const handleToggleGrowth = async () => {
    try {
      setTogglingGrowth(true);
      const newState = !growthStatus?.enabled;
      const payload = {
        enabled: newState,
        daily_growth_percent: growthRate
      };
      // При включении добавляем целевую цену (если указана)
      if (newState && growthTargetPrice) {
        payload.target_price = parseFloat(growthTargetPrice);
      }
      const res = await adminAPI.toggleGrowth(payload);
      if (res.data.success) {
        setSuccess(res.data.message);
        loadData();
      } else {
        setError(res.data.error || 'Ошибка');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка переключения роста');
    } finally {
      setTogglingGrowth(false);
    }
  };

  // Обновить темп роста
  const handleUpdateGrowthRate = async () => {
    try {
      const res = await adminAPI.updateGrowthRate({ daily_growth_percent: growthRate });
      if (res.data.success) {
        setSuccess(res.data.message);
        loadData();
      } else {
        setError(res.data.error || 'Ошибка');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка обновления темпа роста');
    }
  };

  // Сброс Circuit Breaker
  const handleResetCircuitBreaker = async () => {
    try {
      await adminAPI.resetMarketMakerCircuitBreaker();
      setSuccess('Circuit Breaker сброшен');
      loadData();
    } catch (err) {
      setError('Ошибка сброса Circuit Breaker');
    }
  };

  // Добавление/редактирование правила кошелька
  const handleSaveWalletRule = async () => {
    try {
      setSaving(true);
      const { mode, data } = walletDialog;

      if (mode === 'add') {
        await adminAPI.addMarketMakerWallet(data);
      } else {
        await adminAPI.updateMarketMakerWallet(data.id, data);
      }

      setSuccess(mode === 'add' ? 'Правило добавлено' : 'Правило обновлено');
      setWalletDialog({ open: false, mode: 'add', data: null });
      loadWalletRules();
    } catch (err) {
      setError('Ошибка сохранения правила');
    } finally {
      setSaving(false);
    }
  };

  // Удаление правила кошелька
  const handleDeleteWalletRule = async (id) => {
    if (!window.confirm('Удалить правило?')) return;

    try {
      await adminAPI.deleteMarketMakerWallet(id);
      setSuccess('Правило удалено');
      loadWalletRules();
    } catch (err) {
      setError('Ошибка удаления правила');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Загрузка Market Maker...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Market Maker</Typography>
            <Typography color="text.secondary">
              Автоматическое гашение зелёных свечей
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
          >
            Обновить
          </Button>
          <Button
            variant="contained"
            color={status?.isRunning ? 'error' : 'success'}
            startIcon={status?.isRunning ? <Stop /> : <PlayArrow />}
            onClick={handleToggle}
            disabled={saving}
          >
            {status?.isRunning ? 'Остановить' : 'Запустить'}
          </Button>
        </Box>
      </Box>

      {/* Алерты */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Статус карточки */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Статус</Typography>
              <Chip
                icon={status?.isRunning ? <CheckCircle /> : <Error />}
                label={status?.isRunning ? 'Работает' : 'Остановлен'}
                color={status?.isRunning ? 'success' : 'error'}
                size="large"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Текущая цена SPERM</Typography>
              <Typography variant="h5">${formatPrice(price?.spermPriceUSD)}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Баланс TON</Typography>
              <Typography variant="h5">{formatClean(status?.balances?.ton, 4)} TON</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Баланс SPERM</Typography>
              <Typography variant="h5">{formatNumber(status?.balances?.sperm || 0)} SPERM</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Circuit Breaker */}
      {status?.decisionState?.circuitBreaker?.isOpen && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={handleResetCircuitBreaker}>
            Сбросить
          </Button>
        }>
          <strong>Circuit Breaker открыт!</strong> Market Maker приостановлен из-за ошибок.
        </Alert>
      )}

      {/* Вкладки */}
      <Paper sx={{ p: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<TrendingUp />} label="Рост курса" sx={{ color: growthStatus?.enabled ? 'success.main' : 'inherit', fontWeight: growthStatus?.enabled ? 'bold' : 'normal' }} />
          <Tab icon={<Settings />} label="Настройки" />
          <Tab icon={<Person />} label="Кошельки" />
          <Tab icon={<History />} label="История сделок" />
          <Tab icon={<Assessment />} label="Логи" />
          <Tab icon={<Search />} label="Сканер" />
          <Tab icon={<AutoMode />} label="Авто-Флипперы" />
          <Tab icon={<AccountTree />} label="Distribution" />
        </Tabs>

        {/* Вкладка: Настройки */}
        <TabPanel value={tabIndex} index={1}>
          {/* Ссылка на вкладку роста */}
          {growthStatus?.enabled && (
            <Alert
              severity="success"
              sx={{ mb: 2, cursor: 'pointer' }}
              onClick={() => setTabIndex(0)}
              action={<Button color="inherit" size="small" onClick={() => setTabIndex(0)}>Открыть</Button>}
            >
              Режим роста активен (+{growthStatus.dailyGrowthPercent}%/сутки) — подробности во вкладке "Рост курса"
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Основные настройки</Typography>

              <TextField
                fullWidth
                label="Процент гашения (%)"
                type="number"
                value={formatSettingNum(settings?.sell_percentage, 30)}
                onChange={(e) => setSettings({ ...settings, sell_percentage: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
                helperText="Процент от покупки для контр-продажи"
              />

              <TextField
                fullWidth
                label="Мин. объём для срабатывания (SPERM)"
                type="number"
                value={formatSettingNum(settings?.min_volume_trigger, 20)}
                onChange={(e) => setSettings({ ...settings, min_volume_trigger: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
                helperText="Минимальный агрегированный объём для триггера"
              />

              <TextField
                fullWidth
                label="Окно агрегации (секунды)"
                type="number"
                value={formatSettingNum(settings?.aggregation_window_seconds, 30)}
                onChange={(e) => setSettings({ ...settings, aggregation_window_seconds: parseInt(e.target.value) })}
                sx={{ mb: 2 }}
                helperText="Время агрегации мелких покупок"
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Режим работы</InputLabel>
                <Select
                  value={settings?.mode || 'auto'}
                  onChange={(e) => setSettings({ ...settings, mode: e.target.value })}
                  label="Режим работы"
                >
                  <MenuItem value="auto">Авто</MenuItem>
                  <MenuItem value="manual">Ручной</MenuItem>
                  <MenuItem value="paused">Пауза</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Ценовые границы</Typography>

              {growthStatus?.enabled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Режим роста активен — коридор двигается автоматически.
                  Изменение floor/ceiling вручную вступит в силу после выключения роста.
                </Alert>
              )}

              <TextField
                fullWidth
                label="Потолок цены ($)"
                type="number"
                value={settings?.price_ceiling || ''}
                onChange={(e) => setSettings({ ...settings, price_ceiling: e.target.value ? parseFloat(e.target.value) : null })}
                sx={{ mb: 2 }}
                helperText={growthStatus?.enabled
                  ? `Авто-коридор: ceiling = $${formatPrice(growthStatus.currentCeiling)}`
                  : "Продажа при превышении (оставьте пустым для отключения)"
                }
                disabled={growthStatus?.enabled}
              />

              <TextField
                fullWidth
                label="Пол цены ($)"
                type="number"
                value={settings?.price_floor || ''}
                onChange={(e) => setSettings({ ...settings, price_floor: e.target.value ? parseFloat(e.target.value) : null })}
                sx={{ mb: 2 }}
                helperText={growthStatus?.enabled
                  ? `Авто-коридор: floor = $${formatPrice(growthStatus.currentFloor)}`
                  : "Покупка при падении ниже"
                }
                disabled={growthStatus?.enabled}
              />

              <TextField
                fullWidth
                label="Буфер от границы (%)"
                type="number"
                value={settings?.price_buffer_percent || 2}
                onChange={(e) => setSettings({ ...settings, price_buffer_percent: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Лимиты</Typography>

              <TextField
                fullWidth
                label="Макс. одна сделка (SPERM)"
                type="number"
                value={settings?.max_single_trade || 50000}
                onChange={(e) => setSettings({ ...settings, max_single_trade: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Макс. объём в час (SPERM)"
                type="number"
                value={settings?.max_hourly_volume || 200000}
                onChange={(e) => setSettings({ ...settings, max_hourly_volume: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Мин. резерв TON"
                type="number"
                value={settings?.min_reserve_ton || 1}
                onChange={(e) => setSettings({ ...settings, min_reserve_ton: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Мин. резерв SPERM"
                type="number"
                value={settings?.min_reserve_sperm || 10000}
                onChange={(e) => setSettings({ ...settings, min_reserve_sperm: parseFloat(e.target.value) })}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : null}
              >
                Сохранить настройки
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Вкладка: Кошельки */}
        <TabPanel value={tabIndex} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Правила для кошельков</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setWalletDialog({
                open: true,
                mode: 'add',
                data: { wallet_address: '', rule_type: 'custom', sell_percentage: 100, comment: '' }
              })}
            >
              Добавить правило
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Адрес кошелька</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Гашение %</TableCell>
                  <TableCell>Куплено</TableCell>
                  <TableCell>Продано</TableCell>
                  <TableCell>Профит (TON)</TableCell>
                  <TableCell>Комментарий</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {walletRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Tooltip title={rule.wallet_address}>
                        <span>{formatAddress(rule.wallet_address)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={
                          rule.rule_type === 'flipper' ? <Warning /> :
                          rule.rule_type === 'whitelist' ? <CheckCircle /> :
                          <Star />
                        }
                        label={
                          rule.rule_type === 'flipper' ? 'Флиппер' :
                          rule.rule_type === 'whitelist' ? 'Белый список' :
                          'Кастом'
                        }
                        color={
                          rule.rule_type === 'flipper' ? 'error' :
                          rule.rule_type === 'whitelist' ? 'success' :
                          'primary'
                        }
                      />
                    </TableCell>
                    <TableCell>{rule.sell_percentage}%</TableCell>
                    <TableCell>{formatNumber(rule.total_bought || 0)}</TableCell>
                    <TableCell>{formatNumber(rule.total_sold || 0)}</TableCell>
                    <TableCell>{formatClean(rule.profit_estimate, 4)}</TableCell>
                    <TableCell>{rule.comment || '-'}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setWalletDialog({ open: true, mode: 'edit', data: rule })}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteWalletRule(rule.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {walletRules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">Нет правил</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Вкладка: История сделок */}
        <TabPanel value={tabIndex} index={3}>
          <Typography variant="h6" gutterBottom>История сделок бота</Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>SPERM</TableCell>
                  <TableCell>TON</TableCell>
                  <TableCell>Триггер</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>TX Hash</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.map((trade) => (
                  <TableRow key={trade.id}>
                    <TableCell>{formatDate(trade.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={trade.trade_type?.includes('sell') ? <TrendingDown /> : <TrendingUp />}
                        label={
                          trade.trade_type === 'counter_sell' ? 'Контр-продажа' :
                          trade.trade_type === 'ceiling_sell' ? 'Потолок' :
                          trade.trade_type === 'floor_buy' ? 'Пол' :
                          trade.trade_type
                        }
                        color={trade.trade_type?.includes('sell') ? 'error' : 'success'}
                      />
                    </TableCell>
                    <TableCell>{formatNumber(trade.sperm_amount || 0)}</TableCell>
                    <TableCell>{formatClean(trade.ton_amount, 4)}</TableCell>
                    <TableCell>
                      {trade.trigger_wallet ? formatAddress(trade.trigger_wallet) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={trade.status === 'completed' ? 'Выполнена' : trade.status === 'failed' ? 'Ошибка' : trade.status}
                        color={trade.status === 'completed' ? 'success' : trade.status === 'failed' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {trade.tx_hash ? (
                        <Tooltip title={trade.tx_hash}>
                          <span>{trade.tx_hash.substring(0, 10)}...</span>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {trades.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">Нет сделок</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Вкладка: Логи */}
        <TabPanel value={tabIndex} index={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Логи Market Maker</Typography>
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadLogs}>
              Обновить
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Время</TableCell>
                  <TableCell>Уровень</TableCell>
                  <TableCell>Событие</TableCell>
                  <TableCell>Сообщение</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{formatDate(log.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={log.level}
                        color={
                          log.level === 'error' || log.level === 'critical' ? 'error' :
                          log.level === 'warn' ? 'warning' :
                          log.level === 'info' ? 'info' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{formatEventType(log.event_type)}</TableCell>
                    <TableCell>{formatTradeType(log.message)}</TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">Нет логов</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Вкладка: Сканер прибыльных кошельков */}
        <TabPanel value={tabIndex} index={5}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              <Analytics sx={{ mr: 1, verticalAlign: 'middle', fontSize: { xs: 18, sm: 24 } }} />
              Сканер флипперов
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
              Анализ торговой активности для выявления флипперов
            </Typography>

            {/* Параметры сканирования */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={4} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Топ</InputLabel>
                  <Select
                    value={scanParams.limit}
                    onChange={(e) => setScanParams({ ...scanParams, limit: e.target.value })}
                    label="Топ"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Дней</InputLabel>
                  <Select
                    value={scanParams.days}
                    onChange={(e) => setScanParams({ ...scanParams, days: e.target.value })}
                    label="Дней"
                  >
                    <MenuItem value={7}>7</MenuItem>
                    <MenuItem value={14}>14</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                    <MenuItem value={60}>60</MenuItem>
                    <MenuItem value={90}>90</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Сделок</InputLabel>
                  <Select
                    value={scanParams.minTrades}
                    onChange={(e) => setScanParams({ ...scanParams, minTrades: e.target.value })}
                    label="Сделок"
                  >
                    <MenuItem value={2}>≥2</MenuItem>
                    <MenuItem value={3}>≥3</MenuItem>
                    <MenuItem value={5}>≥5</MenuItem>
                    <MenuItem value={10}>≥10</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <Search />}
                  onClick={handleScan}
                  disabled={scanning}
                  sx={{ height: '40px' }}
                >
                  {scanning ? 'Поиск...' : 'Искать'}
                </Button>
              </Grid>
            </Grid>

            {/* Информация о сканировании */}
            {scanMeta && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {scanMeta.source === 'blockchain' ? '🔗 Данные из блокчейна: ' : ''}
                Найдено {scanMeta.total_found} кошельков за {scanMeta.days_analyzed} дней
                {scanMeta.total_events_scanned ? ` (${scanMeta.total_events_scanned} событий проанализировано)` : ''}
              </Alert>
            )}
          </Box>

          {/* Результаты сканирования */}
          {scanResults.length > 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} lg={selectedWallet ? 7 : 12}>
                <TableContainer component={Paper} sx={{ maxHeight: 500, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 30 }}>#</TableCell>
                        <TableCell sx={{ minWidth: 140 }}>Адрес</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'total_trades'}
                            direction={sortConfig.field === 'total_trades' ? sortConfig.direction : 'desc'}
                            onClick={() => handleSort('total_trades')}
                          >
                            Сделок
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 90 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'ton_profit'}
                            direction={sortConfig.field === 'ton_profit' ? sortConfig.direction : 'desc'}
                            onClick={() => handleSort('ton_profit')}
                          >
                            PNL
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 60 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'profit_percent'}
                            direction={sortConfig.field === 'profit_percent' ? sortConfig.direction : 'desc'}
                            onClick={() => handleSort('profit_percent')}
                          >
                            %
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 80 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'last_trade'}
                            direction={sortConfig.field === 'last_trade' ? sortConfig.direction : 'desc'}
                            onClick={() => handleSort('last_trade')}
                          >
                            Когда
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'is_likely_flipper'}
                            direction={sortConfig.field === 'is_likely_flipper' ? sortConfig.direction : 'desc'}
                            onClick={() => handleSort('is_likely_flipper')}
                          >
                            Флип?
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 80 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'project_link'}
                            direction={sortConfig.field === 'project_link' ? sortConfig.direction : 'desc'}
                            onClick={() => handleSort('project_link')}
                          >
                            Связь
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 60 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedScanResults.map((wallet, idx) => (
                        <TableRow
                          key={wallet.wallet_address}
                          hover
                          selected={selectedWallet === wallet.wallet_address}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: wallet.added_as_flipper ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                          }}
                          onClick={() => handleViewWalletDetails(wallet)}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={wallet.wallet_address_friendly || wallet.wallet_address}>
                                <Link
                                  href={wallet.tonscan_url || `https://tonscan.org/address/${wallet.wallet_address_friendly || wallet.wallet_address}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  {formatAddress(wallet.wallet_address_friendly || wallet.wallet_address)}
                                </Link>
                              </Tooltip>
                              <OpenInNew sx={{ fontSize: 12, color: 'text.secondary' }} />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`${wallet.buy_count} покупок / ${wallet.sell_count} продаж`}>
                              <Chip
                                size="small"
                                label={wallet.total_trades}
                                color="default"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`Заработано: ${formatClean(wallet.ton_profit, 4)} TON`}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  color: wallet.ton_profit > 0 ? 'success.main' : 'error.main'
                                }}
                              >
                                {wallet.ton_profit > 0 ? '+' : ''}{formatClean(wallet.ton_profit, 2)} 💎
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ color: wallet.profit_percent > 0 ? 'success.main' : 'error.main' }}
                            >
                              {wallet.profit_percent > 0 ? '+' : ''}{formatClean(wallet.profit_percent, 1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={formatTimestamp(wallet.last_trade)}>
                              <Typography variant="body2" color="text.secondary">
                                {getTimeSince(wallet.last_trade)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            {wallet.is_likely_flipper ? (
                              <Chip size="small" label="Да" color="error" icon={<Warning />} />
                            ) : (
                              <Chip size="small" label="Нет" color="default" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {wallet.project_link?.is_project_user ? (
                              <Tooltip title={
                                `Пользователь: ${wallet.project_link.username || 'ID:' + wallet.project_link.user_id}\n` +
                                (wallet.project_link.deposits?.count > 0 ? `Депозитов: ${wallet.project_link.deposits.count} (${formatClean(wallet.project_link.deposits.total, 2)} SPERM)\n` : '') +
                                (wallet.project_link.stakings?.count > 0 ? `Стейкингов: ${wallet.project_link.stakings.count}\n` : '') +
                                (wallet.project_link.withdrawals?.count > 0 ? `Выводов: ${wallet.project_link.withdrawals.count}` : '')
                              }>
                                <Chip
                                  size="small"
                                  label={wallet.project_link.username || `#${wallet.project_link.user_id}`}
                                  color="info"
                                  icon={<Person />}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            {wallet.added_as_flipper ? (
                              <Chip size="small" label="Добавлен" color="success" />
                            ) : (
                              <Tooltip title="Добавить в флипперы (100% гашение)">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleAddAsFlipper(wallet)}
                                >
                                  <PersonAdd />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Детализация выбранного кошелька */}
              {selectedWallet && (
                <Grid item xs={12} lg={5}>
                  <Paper sx={{ p: 2, maxHeight: { xs: 400, lg: 500 }, overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Typography variant="h6">
                        <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Детализация
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedWallet(null); setWalletDetails(null); }}
                        title="Закрыть"
                      >
                        <Close />
                      </IconButton>
                    </Box>

                    {loadingDetails ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : walletDetails ? (
                      <>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2, wordBreak: 'break-all' }}>
                          {walletDetails.wallet_address}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Всего сделок:</Typography>
                            <Typography variant="body1" fontWeight="bold">{walletDetails.stats?.total_trades || 0}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Покупок/Продаж:</Typography>
                            <Typography variant="body1">{walletDetails.stats?.buy_count || 0} / {walletDetails.stats?.sell_count || 0}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Потрачено TON:</Typography>
                            <Typography variant="body1">{formatClean(walletDetails.stats?.total_spent_ton, 4)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Получено TON:</Typography>
                            <Typography variant="body1">{formatClean(walletDetails.stats?.total_earned_ton, 4)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">PNL (TON):</Typography>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color={walletDetails.stats?.ton_profit > 0 ? 'success.main' : 'error.main'}
                            >
                              {walletDetails.stats?.ton_profit > 0 ? '+' : ''}{formatClean(walletDetails.stats?.ton_profit, 4)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Доходность:</Typography>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color={walletDetails.stats?.profit_percent > 0 ? 'success.main' : 'error.main'}
                            >
                              {walletDetails.stats?.profit_percent > 0 ? '+' : ''}{formatClean(walletDetails.stats?.profit_percent, 1)}%
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Первая сделка:</Typography>
                            <Typography variant="body1">{formatTimestamp(walletDetails.stats?.first_trade)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Последняя сделка:</Typography>
                            <Typography variant="body1">{formatTimestamp(walletDetails.stats?.last_trade)}</Typography>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {walletDetails.existing_rule ? (
                          <Alert severity="success" sx={{ mb: 2 }}>
                            Уже в правилах: {walletDetails.existing_rule.rule_type} ({walletDetails.existing_rule.sell_percentage}%)
                          </Alert>
                        ) : (
                          <Button
                            fullWidth
                            variant="contained"
                            color="error"
                            startIcon={<PersonAdd />}
                            onClick={() => handleAddAsFlipper({ wallet_address: walletDetails.wallet_address, ton_profit: walletDetails.stats?.ton_profit, profit_percent: walletDetails.stats?.profit_percent })}
                          >
                            Добавить в флипперы (100%)
                          </Button>
                        )}

                        {/* Последние сделки */}
                        {walletDetails.trades?.length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Последние сделки:</Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Тип</TableCell>
                                  <TableCell align="right">SPERM</TableCell>
                                  <TableCell align="right">TON</TableCell>
                                  <TableCell>Дата</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {walletDetails.trades.slice(0, 10).map((trade, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={trade.event_type === 'buy' ? 'Покупка' : 'Продажа'}
                                        color={trade.event_type === 'buy' ? 'success' : 'error'}
                                      />
                                    </TableCell>
                                    <TableCell align="right">{formatClean(trade.sperm_amount, 2)}</TableCell>
                                    <TableCell align="right">{formatClean(trade.ton_amount, 4)}</TableCell>
                                    <TableCell>{formatTimestamp(trade.timestamp)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </>
                        )}
                      </>
                    ) : (
                      <Typography color="text.secondary">Выберите кошелёк для просмотра детализации</Typography>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}

          {/* Пустое состояние */}
          {scanResults.length === 0 && !scanning && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Search sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Нажмите "Сканировать" для анализа
              </Typography>
              <Typography color="text.secondary">
                Будут найдены кошельки с положительным PNL за указанный период
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Вкладка: Авто-Флипперы */}
        <TabPanel value={tabIndex} index={6}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                <AutoMode sx={{ mr: 1, verticalAlign: 'middle' }} />
                Автоматический сканер флипперов
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Анализирует последние 7 дней свопов в пуле. Флипперы с confidence ≥90% добавляются автоматически.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={loadingFlipperScan ? <CircularProgress size={20} color="inherit" /> : <ScanIcon />}
              onClick={handleAutoScan}
              disabled={loadingFlipperScan}
            >
              {loadingFlipperScan ? 'Сканирование...' : 'Сканировать сейчас'}
            </Button>
          </Box>

          {/* Результат последнего сканирования */}
          {lastScanResult && (
            <Alert
              severity={lastScanResult.found > 0 ? 'success' : 'info'}
              sx={{ mb: 2 }}
              onClose={() => setLastScanResult(null)}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {lastScanResult.message || (lastScanResult.found > 0
                  ? `Найдено ${lastScanResult.found} новых флипперов`
                  : 'Новых флипперов не найдено')}
              </Typography>
              {lastScanResult.scanStats && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  📊 Проанализировано: {lastScanResult.scanStats.eventsScanned} событий за {lastScanResult.scanStats.daysAnalyzed} дней |
                  👛 Кошельков: {lastScanResult.scanStats.walletsAnalyzed} |
                  🎯 Потенциальных флипперов: {lastScanResult.scanStats.potentialFlippers}
                  {lastScanResult.scanStats.alreadyKnown > 0 && ` | ✅ Уже известны: ${lastScanResult.scanStats.alreadyKnown}`}
                </Typography>
              )}
            </Alert>
          )}

          {/* Статистика */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ py: 1 }}>
                  <Typography color="text.secondary" variant="body2">Ожидают проверки</Typography>
                  <Typography variant="h5" color="warning.main">{flipperStats?.pending || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ py: 1 }}>
                  <Typography color="text.secondary" variant="body2">Одобрено</Typography>
                  <Typography variant="h5" color="success.main">{flipperStats?.approved || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ py: 1 }}>
                  <Typography color="text.secondary" variant="body2">Авто-добавлено</Typography>
                  <Typography variant="h5" color="info.main">{flipperStats?.auto_added || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ py: 1 }}>
                  <Typography color="text.secondary" variant="body2">Отклонено</Typography>
                  <Typography variant="h5" color="error.main">{flipperStats?.rejected || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Информация о сканере */}
          {flipperScannerStats && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Всего сканирований: {flipperScannerStats.totalScans} |
              Найдено флипперов: {flipperScannerStats.flippersFound} |
              Авто-добавлено: {flipperScannerStats.autoAdded} |
              {flipperScannerStats.lastScanTime && ` Последнее: ${formatDate(flipperScannerStats.lastScanTime)}`}
            </Alert>
          )}

          {/* Фильтр по статусу */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Показать:</Typography>
            {[
              { value: 'pending', label: 'Ожидают', color: 'warning' },
              { value: 'all', label: 'Все', color: 'default' },
              { value: 'approved', label: 'Одобренные', color: 'success' },
              { value: 'auto_added', label: 'Авто', color: 'info' },
              { value: 'rejected', label: 'Отклонённые', color: 'error' }
            ].map(f => (
              <Chip
                key={f.value}
                label={f.label}
                size="small"
                variant={flipperStatusFilter === f.value ? 'filled' : 'outlined'}
                color={f.color}
                onClick={() => setFlipperStatusFilter(f.value)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Показано: {filteredCandidates.length} из {flipperCandidates.length}
            </Typography>
          </Box>

          {/* Таблица кандидатов */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Адрес</TableCell>
                  <TableCell align="center">Покупки</TableCell>
                  <TableCell align="center">Продажи</TableCell>
                  <TableCell align="right">Профит (TON)</TableCell>
                  <TableCell align="center">Confidence</TableCell>
                  <TableCell align="center">Статус</TableCell>
                  <TableCell>Обнаружен</TableCell>
                  <TableCell align="center">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCandidates.map((candidate) => (
                  <TableRow key={candidate.id} hover>
                    <TableCell>
                      <Link
                        href={`https://tonscan.org/address/${candidate.wallet_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                      >
                        {formatAddress(candidate.wallet_address)}
                      </Link>
                    </TableCell>
                    <TableCell align="center">{candidate.buy_count}</TableCell>
                    <TableCell align="center">{candidate.sell_count}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: parseFloat(candidate.total_profit) > 0 ? 'success.main' : 'error.main'
                        }}
                      >
                        {formatClean(candidate.total_profit, 2)} 💎
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${candidate.confidence_score}%`}
                        color={
                          candidate.confidence_score >= 90 ? 'success' :
                          candidate.confidence_score >= 70 ? 'warning' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          candidate.status === 'pending' ? 'Ожидает' :
                          candidate.status === 'approved' ? 'Одобрен' :
                          candidate.status === 'auto_added' ? 'Авто' :
                          candidate.status === 'rejected' ? 'Отклонён' :
                          candidate.status
                        }
                        color={
                          candidate.status === 'pending' ? 'warning' :
                          candidate.status === 'approved' || candidate.status === 'auto_added' ? 'success' :
                          candidate.status === 'rejected' ? 'error' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{formatDate(candidate.detected_at)}</TableCell>
                    <TableCell align="center">
                      {candidate.status === 'pending' && (
                        <>
                          <Tooltip title="Одобрить (добавить в флипперы)">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveCandidate(candidate.id)}
                            >
                              <ThumbUp />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Отклонить">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectCandidate(candidate.id)}
                            >
                              <ThumbDown />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Удалить">
                        <IconButton
                          size="small"
                          color="default"
                          onClick={() => handleDeleteCandidate(candidate.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCandidates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 4 }}>
                        <AutoMode sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                          {flipperCandidates.length === 0
                            ? 'Нет кандидатов. Нажмите "Сканировать сейчас" для поиска флипперов.'
                            : `Нет кандидатов со статусом "${flipperStatusFilter}". Попробуйте другой фильтр.`}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* Вкладка: Рост курса (полная визуализация) */}
        {/* ═══════════════════════════════════════════════════════ */}
        {/* Вкладка: Distribution */}
        <TabPanel value={tabIndex} index={7}>
          {distLoading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Статус и прогресс */}
          {distStatus?.state && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Прогресс распределения</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={distStatus.state.progress || 0}
                      sx={{ height: 12, borderRadius: 6, mb: 1 }}
                      color={distStatus.state.status === 'completed' ? 'success' : distStatus.state.status === 'stopped' ? 'error' : 'primary'}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">
                        Фаза: <strong>{distStatus.state.status || 'idle'}</strong> (этап {distStatus.state.phase || 0}/6)
                      </Typography>
                      <Typography variant="body2">
                        <strong>{Math.round(distStatus.state.progress || 0)}%</strong>
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">Кошельков</Typography>
                        <Typography variant="h6">{distStatus.state.total_wallets || 0}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">Профинанс.</Typography>
                        <Typography variant="h6">{distStatus.state.funded_wallets || 0}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">Прогрето</Typography>
                        <Typography variant="h6">{distStatus.state.warmed_wallets || 0}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">SPERM отправлено</Typography>
                        <Typography variant="h6">{formatNumber(distStatus.state.total_sperm_distributed || 0)}</Typography>
                      </Grid>
                    </Grid>
                    {distStatus.state.last_error && (
                      <Alert severity="error" sx={{ mt: 1 }}>{distStatus.state.last_error}</Alert>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Управление</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PlayArrow />}
                        onClick={handleDistStart}
                        disabled={distStatus.isRunning}
                        fullWidth
                      >
                        Запустить распределение
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<Stop />}
                        onClick={handleDistStop}
                        disabled={!distStatus.isRunning}
                        fullWidth
                      >
                        Остановить
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Shuffle />}
                        onClick={handleDistActivity}
                        disabled={distStatus.state.status !== 'completed'}
                        fullWidth
                      >
                        Рандомная активность
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<VpnKey />}
                        onClick={() => setDistExportDialog(true)}
                        disabled={!distStatus.state.total_wallets}
                        fullWidth
                      >
                        Экспорт seed-фраз
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setDistSweepDialog(true)}
                        disabled={!distStatus.state.total_wallets}
                        fullWidth
                      >
                        Собрать все монеты на 1 кошелёк
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        startIcon={distRefreshLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                        onClick={handleDistRefreshBalances}
                        disabled={distRefreshLoading || !distStatus.state.total_wallets}
                        fullWidth
                      >
                        {distRefreshLoading ? 'Обновление балансов...' : 'Обновить все балансы'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {distRefreshLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Идёт обновление балансов с блокчейна... Это может занять 20-40 секунд.
            </Alert>
          )}

          {/* Таблица кошельков */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6">Кошельки ({distWalletsTotal})</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {distSort !== 'default' && (
                  <Chip
                    label={`Сорт: ${distSort === 'ton' ? 'TON' : 'SPERM'} ${distSortDir === 'desc' ? '↓' : '↑'}`}
                    size="small"
                    onDelete={() => { setDistSort('default'); setDistSortDir('asc'); setDistWalletPage(1); }}
                  />
                )}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Tier</InputLabel>
                <Select
                  value={distTierFilter}
                  label="Tier"
                  onChange={(e) => { setDistTierFilter(e.target.value); setDistWalletPage(1); }}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="hub">Hub (7)</MenuItem>
                  <MenuItem value="relay">Relay (20)</MenuItem>
                  <MenuItem value="leaf">Leaf (100)</MenuItem>
                </Select>
              </FormControl>
              </Box>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Адрес</TableCell>
                    <TableCell align="right" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleDistSortToggle('ton')}>
                      <TableSortLabel active={distSort === 'ton'} direction={distSort === 'ton' ? distSortDir : 'desc'}>
                        TON
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleDistSortToggle('sperm')}>
                      <TableSortLabel active={distSort === 'sperm'} direction={distSort === 'sperm' ? distSortDir : 'desc'}>
                        SPERM
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Свапы</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {distWallets.length > 0 ? distWallets.map(w => (
                    <TableRow key={w.id}>
                      <TableCell>{w.id}</TableCell>
                      <TableCell>
                        <Chip
                          label={w.tier}
                          size="small"
                          color={w.tier === 'hub' ? 'primary' : w.tier === 'relay' ? 'secondary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          href={`https://tonviewer.com/${w.address}`}
                          target="_blank"
                          rel="noopener"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {formatAddress(w.address)}
                        </Link>
                      </TableCell>
                      <TableCell align="right">
                        {distOnchainBalances[w.id]
                          ? <strong>{distOnchainBalances[w.id].ton?.toFixed(3)}</strong>
                          : (w.ton_balance != null ? formatClean(w.ton_balance, 3) : '-')
                        }
                      </TableCell>
                      <TableCell align="right">
                        {distOnchainBalances[w.id]
                          ? <strong>{formatNumber(distOnchainBalances[w.id].sperm || 0)}</strong>
                          : (w.sperm_balance != null ? formatNumber(w.sperm_balance) : '?')
                        }
                      </TableCell>
                      <TableCell align="center">{w.swaps_done || 0}/{w.swaps_planned || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={w.status || 'pending'}
                          size="small"
                          color={
                            w.status === 'completed' ? 'success' :
                            w.status === 'funded' ? 'info' :
                            w.status === 'warming' ? 'warning' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleDistCheckBalance(w.id)}
                          disabled={distBalanceLoading[w.id]}
                          sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                        >
                          {distBalanceLoading[w.id] ? '...' : 'Bal'}
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={() => openDistSendDialog(w.id, 'ton')}
                          sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                        >
                          TON
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="secondary"
                          onClick={() => openDistSendDialog(w.id, 'sperm')}
                          sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                        >
                          SPERM
                        </Button>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">Нет кошельков</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {distWalletsTotal > 50 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 1 }}>
                <Button size="small" disabled={distWalletPage <= 1} onClick={() => setDistWalletPage(p => p - 1)}>Назад</Button>
                <Typography variant="body2" sx={{ lineHeight: '30px' }}>
                  Стр. {distWalletPage} из {Math.ceil(distWalletsTotal / 50)}
                </Typography>
                <Button size="small" disabled={distWalletPage >= Math.ceil(distWalletsTotal / 50)} onClick={() => setDistWalletPage(p => p + 1)}>Вперёд</Button>
              </Box>
            )}
          </Paper>

          {/* Логи операций */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Логи ({distLogsTotal})</Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Время</TableCell>
                    <TableCell>Кошелёк</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Детали</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {distLogs.length > 0 ? distLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(log.created_at)}</TableCell>
                      <TableCell>
                        {log.tier && <Chip label={log.tier} size="small" sx={{ mr: 0.5 }} />}
                        {log.address ? formatAddress(log.address) : `#${log.wallet_id}`}
                      </TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={log.status === 'success' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.error_message || (log.details ? JSON.stringify(log.details).slice(0, 80) : '-')}
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">Нет логов</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {distLogsTotal > 30 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 1 }}>
                <Button size="small" disabled={distLogPage <= 1} onClick={() => setDistLogPage(p => p - 1)}>Назад</Button>
                <Typography variant="body2" sx={{ lineHeight: '30px' }}>
                  Стр. {distLogPage} из {Math.ceil(distLogsTotal / 30)}
                </Typography>
                <Button size="small" disabled={distLogPage >= Math.ceil(distLogsTotal / 30)} onClick={() => setDistLogPage(p => p + 1)}>Вперёд</Button>
              </Box>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value={tabIndex} index={0}>
          {/* Заголовок и управление */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TrendingUp sx={{ color: growthStatus?.enabled ? 'success.main' : 'text.secondary' }} />
                Плавный рост курса
                <Chip
                  label={growthStatus?.enabled ? 'АКТИВЕН' : 'ВЫКЛЮЧЕН'}
                  color={growthStatus?.enabled ? 'success' : 'default'}
                  size="small"
                />
              </Typography>
              <Typography color="text.secondary" variant="body2">
                MM автоматически поднимает ценовой коридор на заданный % в сутки
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} size="small">
                Обновить
              </Button>
              <Button
                variant="contained"
                color={growthStatus?.enabled ? 'error' : 'success'}
                startIcon={togglingGrowth ? <CircularProgress size={18} color="inherit" /> : (growthStatus?.enabled ? <Stop /> : <PlayArrow />)}
                onClick={handleToggleGrowth}
                disabled={togglingGrowth}
              >
                {growthStatus?.enabled ? 'Остановить рост' : 'Запустить рост'}
              </Button>
            </Box>
          </Box>

          {/* Панель настройки (видна когда рост НЕ активен) */}
          {!growthStatus?.enabled && (
            <Paper elevation={2} sx={{ p: 3, mb: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" gutterBottom>Настройка сценария роста</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Рост в сутки (%)"
                    type="number"
                    value={growthRate}
                    onChange={(e) => setGrowthRate(parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0.1, max: 50, step: 0.1 }}
                    helperText="Как быстро коридор двигается вверх"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Целевая цена ($)"
                    type="number"
                    value={growthTargetPrice}
                    onChange={(e) => setGrowthTargetPrice(e.target.value)}
                    inputProps={{ min: 0, step: 0.001 }}
                    helperText="Авто-стоп при достижении (пусто = бесконечный рост)"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">Текущая цена</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                      ${formatPrice(price?.spermPriceUSD)}
                    </Typography>
                    {growthTargetPrice && price?.spermPriceUSD ? (
                      <Typography variant="caption" color="text.secondary">
                        До цели: +{(((parseFloat(growthTargetPrice) / price.spermPriceUSD) - 1) * 100).toFixed(1)}%
                        {' '}(~{(Math.log(parseFloat(growthTargetPrice) / price.spermPriceUSD) / Math.log(1 + growthRate / 100)).toFixed(1)} дн)
                      </Typography>
                    ) : null}
                  </Box>
                </Grid>
              </Grid>

              {/* Превью графика до запуска */}
              {growthTargetPrice && price?.spermPriceUSD ? (() => {
                const baseP = price.spermPriceUSD;
                const targetP = parseFloat(growthTargetPrice);
                const rate = growthRate / 100;
                const totalDays = Math.log(targetP / baseP) / Math.log(1 + rate);
                const previewData = [];
                const steps = Math.min(Math.ceil(totalDays), 60);
                for (let d = 0; d <= steps; d++) {
                  const factor = Math.pow(1 + rate, d);
                  previewData.push({
                    day: d,
                    label: `День ${d}`,
                    plan: parseFloat((baseP * factor).toFixed(6)),
                    target: targetP
                  });
                }
                return (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Превью: плановая кривая роста ({steps} дн)
                    </Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={previewData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                        <defs>
                          <linearGradient id="growthPreviewGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} domain={['auto', 'auto']} />
                        <RechartsTooltip formatter={(v) => [`$${v}`, '']} />
                        <Area type="monotone" dataKey="plan" stroke="#4CAF50" fill="url(#growthPreviewGrad)" strokeWidth={2} name="План" />
                        <ReferenceLine y={targetP} stroke="#FF5722" strokeDasharray="5 5" label={{ value: `Цель $${targetP}`, fill: '#FF5722', fontSize: 11 }} />
                        <ReferenceLine y={baseP} stroke="#2196F3" strokeDasharray="3 3" label={{ value: `Старт $${baseP.toFixed(4)}`, fill: '#2196F3', fontSize: 11 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                );
              })() : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Укажите целевую цену для просмотра графика роста. Без целевой цены рост будет бесконечным.
                </Alert>
              )}
            </Paper>
          )}

          {/* Активный рост — полная визуализация */}
          {growthStatus?.enabled && (
            <>
              {/* ГЛАВНАЯ КАРТОЧКА — Плановая цена СЕЙЧАС */}
              <Paper elevation={3} sx={{
                p: 2.5, mb: 3,
                background: 'linear-gradient(135deg, rgba(33,150,243,0.08) 0%, rgba(76,175,80,0.08) 100%)',
                border: '2px solid',
                borderColor: (() => {
                  const dev = growthStatus.deviationPercent || 0;
                  if (Math.abs(dev) < 2) return 'success.main';
                  if (Math.abs(dev) < 5) return 'warning.main';
                  return 'error.main';
                })(),
                borderRadius: 3
              }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Плановая цена */}
                  <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="overline" sx={{ fontSize: '0.7rem', letterSpacing: 1, color: 'text.secondary' }}>
                      ПЛАНОВАЯ ЦЕНА СЕЙЧАС
                    </Typography>
                    <Typography variant="h3" sx={{
                      fontFamily: 'monospace', fontWeight: 'bold', color: 'primary.main', lineHeight: 1.2
                    }}>
                      ${formatPrice(growthStatus.momentTargetPrice)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      по плану +{growthStatus.dailyGrowthPercent}%/сутки
                    </Typography>
                  </Grid>

                  {/* Реальная цена */}
                  <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="overline" sx={{ fontSize: '0.7rem', letterSpacing: 1, color: 'text.secondary' }}>
                      РЕАЛЬНАЯ ЦЕНА
                    </Typography>
                    <Typography variant="h3" sx={{
                      fontFamily: 'monospace', fontWeight: 'bold',
                      color: growthStatus.onTrack ? 'success.main' : 'error.main',
                      lineHeight: 1.2
                    }}>
                      ${formatPrice(growthStatus.currentPrice)}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: (growthStatus.deviationPercent || 0) >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'bold', fontSize: '0.85rem'
                    }}>
                      {(growthStatus.deviationPercent || 0) >= 0 ? '▲' : '▼'} {(growthStatus.deviationPercent || 0) >= 0 ? '+' : ''}{(growthStatus.deviationPercent || 0).toFixed(2)}% от плана
                    </Typography>
                  </Grid>

                  {/* Сводка по суткам */}
                  <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="overline" sx={{ fontSize: '0.7rem', letterSpacing: 1, color: 'text.secondary' }}>
                      СЕГОДНЯ (СУТКИ)
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Начало</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          ${formatPrice(growthStatus.todayStartPrice)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>→</Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Конец</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          ${formatPrice(growthStatus.todayEndPrice)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Проверка каждые {growthStatus.priceCheckIntervalSec || 60} сек
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Карточки метрик */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ bgcolor: 'rgba(33,150,243,0.05)', border: '1px solid rgba(33,150,243,0.15)' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Стартовая цена</Typography>
                      <Typography variant="h5" sx={{ fontFamily: 'monospace', color: 'info.main' }}>
                        ${formatPrice(growthStatus.basePrice)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {growthStatus.startedAt ? new Date(growthStatus.startedAt).toLocaleDateString('ru-RU') : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{
                    bgcolor: growthStatus.onTrack ? 'rgba(76,175,80,0.05)' : 'rgba(255,152,0,0.05)',
                    border: growthStatus.onTrack ? '1px solid rgba(76,175,80,0.2)' : '1px solid rgba(255,152,0,0.2)'
                  }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Текущая цена</Typography>
                      <Typography variant="h5" sx={{
                        fontFamily: 'monospace',
                        color: growthStatus.onTrack ? 'success.main' : 'warning.main'
                      }}>
                        ${formatPrice(growthStatus.currentPrice)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: (growthStatus.realGrowthPercent || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {(growthStatus.realGrowthPercent || 0) >= 0 ? '+' : ''}{(growthStatus.realGrowthPercent || 0).toFixed(2)}% реальный
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ bgcolor: 'rgba(255,87,34,0.05)', border: '1px solid rgba(255,87,34,0.15)' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Целевая цена</Typography>
                      <Typography variant="h5" sx={{ fontFamily: 'monospace', color: '#FF5722' }}>
                        {growthStatus.finalTargetPrice ? `$${formatPrice(growthStatus.finalTargetPrice)}` : '∞'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {growthStatus.finalTargetPrice ? `x${(growthStatus.finalTargetPrice / growthStatus.basePrice).toFixed(2)} от старта` : 'Бесконечный рост'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ bgcolor: 'rgba(156,39,176,0.05)', border: '1px solid rgba(156,39,176,0.15)' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Темп роста</Typography>
                      <Typography variant="h5" sx={{ fontFamily: 'monospace', color: '#9C27B0' }}>
                        +{growthStatus.dailyGrowthPercent}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">в сутки</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Прогресс к цели */}
              {growthStatus.finalTargetPrice && (
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Прогресс к цели: ${formatPrice(growthStatus.basePrice)} → ${formatPrice(growthStatus.finalTargetPrice)}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {(growthStatus.progressToTarget || 0).toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(growthStatus.progressToTarget || 0, 100)}
                    sx={{
                      height: 16, borderRadius: 8,
                      bgcolor: 'rgba(0,0,0,0.06)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #4CAF50 0%, #8BC34A 40%, #CDDC39 70%, #FF9800 90%, #FF5722 100%)',
                        borderRadius: 8,
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Прошло: {(growthStatus.elapsedDays || 0) >= 1
                        ? `${Math.floor(growthStatus.elapsedDays)} дн ${Math.floor((growthStatus.elapsedHours || 0) % 24)} ч`
                        : `${Math.floor(growthStatus.elapsedHours || 0)} ч ${Math.floor(((growthStatus.elapsedHours || 0) % 1) * 60)} мин`
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {growthStatus.remainingDays != null
                        ? `Осталось: ~${growthStatus.remainingDays >= 1 ? `${Math.floor(growthStatus.remainingDays)} дн ${Math.floor((growthStatus.remainingDays % 1) * 24)} ч` : `${Math.floor(growthStatus.remainingDays * 24)} ч`}`
                        : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {growthStatus.totalDaysEstimate ? `Всего: ~${growthStatus.totalDaysEstimate.toFixed(1)} дн` : ''}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* График роста */}
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  График сценария роста
                </Typography>
                {(() => {
                  const gs = growthStatus;
                  const baseP = gs.basePrice;
                  const targetP = gs.finalTargetPrice;
                  const rate = gs.dailyGrowthPercent / 100;
                  const elapsedDays = gs.elapsedDays || 0;

                  // Определяем диапазон графика
                  let totalChartDays;
                  if (targetP) {
                    totalChartDays = Math.ceil(Math.log(targetP / baseP) / Math.log(1 + rate)) + 2;
                  } else {
                    totalChartDays = Math.max(Math.ceil(elapsedDays) + 7, 14);
                  }
                  totalChartDays = Math.min(totalChartDays, 120);

                  const chartData = [];
                  for (let d = 0; d <= totalChartDays; d++) {
                    const factor = Math.pow(1 + rate, d);
                    const planPrice = baseP * factor;
                    const floorPrice = (gs.currentFloor / Math.pow(1 + rate, elapsedDays)) * factor;
                    const ceilingPrice = (gs.currentCeiling / Math.pow(1 + rate, elapsedDays)) * factor;

                    const point = {
                      day: d,
                      plan: parseFloat(planPrice.toFixed(6)),
                      floor: parseFloat(floorPrice.toFixed(6)),
                      ceiling: parseFloat(ceilingPrice.toFixed(6))
                    };

                    // Отметка текущего дня (реальная цена)
                    if (Math.abs(d - elapsedDays) < 0.6) {
                      point.actual = gs.currentPrice;
                    }

                    chartData.push(point);
                  }

                  return (
                    <ResponsiveContainer width="100%" height={350}>
                      <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                        <defs>
                          <linearGradient id="corridorGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 11 }}
                          label={{ value: 'Дни', position: 'insideBottomRight', offset: -5, fontSize: 11 }}
                        />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${v}`}
                          domain={['auto', 'auto']}
                        />
                        <RechartsTooltip
                          formatter={(v, name) => {
                            const labels = { plan: 'План', floor: 'Пол', ceiling: 'Потолок', actual: 'Реальная цена' };
                            return [`$${v}`, labels[name] || name];
                          }}
                          labelFormatter={(v) => `День ${v}`}
                        />
                        {/* Коридор (ceiling) */}
                        <Area type="monotone" dataKey="ceiling" stroke="#A5D6A7" strokeWidth={1} strokeDasharray="4 2" fill="none" name="ceiling" dot={false} />
                        {/* Коридор (floor) */}
                        <Area type="monotone" dataKey="floor" stroke="#A5D6A7" strokeWidth={1} strokeDasharray="4 2" fill="url(#corridorGrad)" name="floor" dot={false} />
                        {/* Плановая кривая */}
                        <Line type="monotone" dataKey="plan" stroke="#4CAF50" strokeWidth={2.5} dot={false} name="plan" />
                        {/* Реальная цена (точка) */}
                        <Line type="monotone" dataKey="actual" stroke="#FF5722" strokeWidth={0} dot={{ r: 6, fill: '#FF5722', stroke: '#fff', strokeWidth: 2 }} name="actual" />
                        {/* Целевая цена */}
                        {targetP && (
                          <ReferenceLine
                            y={targetP}
                            stroke="#FF5722"
                            strokeDasharray="6 3"
                            label={{ value: `Цель $${targetP}`, fill: '#FF5722', fontSize: 12, position: 'right' }}
                          />
                        )}
                        {/* Стартовая цена */}
                        <ReferenceLine
                          y={baseP}
                          stroke="#2196F3"
                          strokeDasharray="3 3"
                          label={{ value: `Старт $${baseP.toFixed(4)}`, fill: '#2196F3', fontSize: 11, position: 'left' }}
                        />
                        {/* Вертикальная линия "сейчас" */}
                        <ReferenceLine
                          x={Math.round(elapsedDays)}
                          stroke="#9C27B0"
                          strokeDasharray="4 4"
                          label={{ value: 'Сейчас', fill: '#9C27B0', fontSize: 11, position: 'top' }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  );
                })()}
              </Paper>

              {/* Детальная информация */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Текущий коридор</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Пол (Floor)</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'success.main' }}>
                          ${formatPrice(growthStatus.currentFloor)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Потолок (Ceiling)</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'error.main' }}>
                          ${formatPrice(growthStatus.currentCeiling)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Ширина коридора</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          ${(growthStatus.currentCeiling - growthStatus.currentFloor).toFixed(6)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Коридор через план</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          +{growthStatus.totalGrowthPercent?.toFixed(3)}% от старта
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Управление на лету</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      <TextField
                        label="% в сутки"
                        type="number"
                        size="small"
                        value={growthRate}
                        onChange={(e) => setGrowthRate(parseFloat(e.target.value) || 0)}
                        sx={{ width: 130 }}
                        inputProps={{ min: 0.1, max: 50, step: 0.1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleUpdateGrowthRate}
                        disabled={togglingGrowth}
                      >
                        Применить
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Изменение темпа применяется мгновенно (без перезапуска)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {/* Статус */}
                    {growthStatus.onTrack ? (
                      <Alert severity="success" sx={{ py: 0 }} icon={<CheckCircle fontSize="small" />}>
                        Цена на уровне плана — MM поддерживает рост
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ py: 0 }} icon={<Warning fontSize="small" />}>
                        Цена ниже плана — MM делает floor_buy
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>
      </Paper>

      {/* Диалог экспорта seed-фраз */}
      <Dialog open={distExportDialog} onClose={() => setDistExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Экспорт seed-фраз</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Файл будет зашифрован вашим паролем (AES-256-GCM + PBKDF2). Сохраните пароль — без него расшифровать невозможно!
          </Alert>
          <TextField
            fullWidth
            type="password"
            label="Пароль (минимум 8 символов)"
            value={distExportPassword}
            onChange={(e) => setDistExportPassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDistExportDialog(false); setDistExportPassword(''); }}>Отмена</Button>
          <Button
            variant="contained"
            startIcon={distExportLoading ? <CircularProgress size={16} /> : <Download />}
            onClick={handleDistExport}
            disabled={distExportLoading || distExportPassword.length < 8}
          >
            Скачать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог отправки TON/SPERM с кошелька */}
      <Dialog open={distSendDialog} onClose={() => setDistSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Отправить {distSendData.currency?.toUpperCase()} с кошелька #{distSendData.walletId}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Валюта</InputLabel>
              <Select
                value={distSendData.currency}
                label="Валюта"
                onChange={(e) => setDistSendData(prev => ({ ...prev, currency: e.target.value }))}
              >
                <MenuItem value="ton">TON</MenuItem>
                <MenuItem value="sperm">SPERM</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Адрес получателя"
              value={distSendData.toAddress}
              onChange={(e) => setDistSendData(prev => ({ ...prev, toAddress: e.target.value }))}
              placeholder="UQ... или EQ..."
            />
            <TextField
              fullWidth
              label={`Сумма (${distSendData.currency?.toUpperCase()})`}
              type="number"
              value={distSendData.amount}
              onChange={(e) => setDistSendData(prev => ({ ...prev, amount: e.target.value }))}
            />
            {distSendData.currency === 'sperm' && (
              <TextField
                fullWidth
                label="Комментарий (необязательно)"
                value={distSendData.comment}
                onChange={(e) => setDistSendData(prev => ({ ...prev, comment: e.target.value }))}
              />
            )}
            {distSendLoading && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Отправка {distSendData.currency?.toUpperCase()}... Ожидание подтверждения (до 60 сек)
              </Alert>
            )}
            {distSendError && (
              <Alert severity="error" onClose={() => setDistSendError('')}>{distSendError}</Alert>
            )}
            {distSendSuccess && (
              <Alert severity="success" onClose={() => setDistSendSuccess('')}>{distSendSuccess}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDistSendDialog(false); setDistSendError(''); setDistSendSuccess(''); }} disabled={distSendLoading}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleDistSend}
            disabled={distSendLoading || !distSendData.toAddress || !distSendData.amount || parseFloat(distSendData.amount) <= 0}
          >
            {distSendLoading ? <CircularProgress size={16} /> : 'Отправить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог Sweep с live-прогрессом */}
      <Dialog open={distSweepDialog} onClose={() => !distSweepLoading && setDistSweepDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Собрать монеты на 1 кошелёк</DialogTitle>
        <DialogContent>
          {!sweepProgress ? (
            /* === Фаза 1: Настройка === */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2">Что собрать:</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant={distSweepCurrencies.includes('ton') ? 'contained' : 'outlined'} onClick={() => setDistSweepCurrencies(prev => prev.includes('ton') ? prev.filter(c => c !== 'ton') : [...prev, 'ton'])} fullWidth>TON</Button>
                <Button variant={distSweepCurrencies.includes('sperm') ? 'contained' : 'outlined'} color="secondary" onClick={() => setDistSweepCurrencies(prev => prev.includes('sperm') ? prev.filter(c => c !== 'sperm') : [...prev, 'sperm'])} fullWidth>SPERM</Button>
              </Box>
              <TextField fullWidth label="Куда собрать (адрес кошелька)" value={distSweepTarget} onChange={(e) => setDistSweepTarget(e.target.value)} placeholder="UQ... или EQ..." />
            </Box>
          ) : (
            /* === Фаза 2: Live-прогресс === */
            <Box sx={{ mt: 1 }}>
              {/* Прогресс-бар */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {sweepProgress.status === 'running' ? `Обработка ${sweepProgress.processed}/${sweepProgress.total}` : sweepProgress.status === 'completed' ? 'Завершено' : 'Остановлено'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sweepProgress.status === 'running' && sweepProgress.processed > 0 ? (() => {
                      const elapsed = Date.now() - sweepProgress.startedAt;
                      const avg = elapsed / sweepProgress.processed;
                      const remaining = avg * (sweepProgress.total - sweepProgress.processed);
                      const mins = Math.ceil(remaining / 60000);
                      return `~${mins} мин`;
                    })() : ''}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={sweepProgress.total ? (sweepProgress.processed / sweepProgress.total) * 100 : 0} sx={{ height: 8, borderRadius: 1 }} />
              </Box>

              {/* Текущий кошелёк */}
              {sweepProgress.currentWallet && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Сейчас: #{sweepProgress.currentWallet.id} ({sweepProgress.currentWallet.tier}) — {formatAddress(sweepProgress.currentWallet.address)}
                </Alert>
              )}

              {/* Статистика */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {sweepProgress.currencies?.includes('ton') && (<>
                  <Chip label={`TON собрано: ${sweepProgress.stats.tonSent}`} color="success" size="small" />
                  <Chip label={`TON пропущено: ${sweepProgress.stats.tonSkipped}`} size="small" />
                  {sweepProgress.stats.tonFailed > 0 && <Chip label={`TON ошибок: ${sweepProgress.stats.tonFailed}`} color="error" size="small" />}
                  {sweepProgress.stats.tonAmount > 0 && <Chip label={`Итого: ${sweepProgress.stats.tonAmount.toFixed(3)} TON`} color="primary" size="small" variant="outlined" />}
                </>)}
                {sweepProgress.currencies?.includes('sperm') && (<>
                  <Chip label={`SPERM собрано: ${sweepProgress.stats.spermSent}`} color="success" size="small" />
                  <Chip label={`SPERM пропущено: ${sweepProgress.stats.spermSkipped}`} size="small" />
                  {sweepProgress.stats.spermFailed > 0 && <Chip label={`SPERM ошибок: ${sweepProgress.stats.spermFailed}`} color="error" size="small" />}
                  {sweepProgress.stats.spermAmount > 0 && <Chip label={`Итого: ${formatNumber(sweepProgress.stats.spermAmount)} SPERM`} color="secondary" size="small" variant="outlined" />}
                </>)}
              </Box>

              {/* Лог операций */}
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Лог ({sweepProgress.results.length}):</Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Tier</TableCell>
                      {sweepProgress.currencies?.includes('ton') && <TableCell>TON</TableCell>}
                      {sweepProgress.currencies?.includes('sperm') && <TableCell>SPERM</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...sweepProgress.results].reverse().map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>#{r.walletId}</TableCell>
                        <TableCell><Chip label={r.tier} size="small" /></TableCell>
                        {sweepProgress.currencies?.includes('ton') && (
                          <TableCell>
                            {r.ton === 'sent' ? <Chip label={`${r.tonAmount?.toFixed(3)} TON`} color="success" size="small" /> :
                             r.ton === 'skip' ? <Typography variant="caption" color="text.secondary">пусто</Typography> :
                             r.ton === 'error' ? <Chip label={r.tonError?.substring(0, 30)} color="error" size="small" /> : '-'}
                          </TableCell>
                        )}
                        {sweepProgress.currencies?.includes('sperm') && (
                          <TableCell>
                            {r.sperm === 'sent' ? <Chip label={`${formatNumber(r.spermAmount)} SPERM`} color="success" size="small" /> :
                             r.sperm === 'skip' ? <Typography variant="caption" color="text.secondary">пусто</Typography> :
                             r.sperm === 'error' ? <Chip label={r.spermError?.substring(0, 30)} color="error" size="small" /> : '-'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!sweepProgress ? (<>
            <Button onClick={() => setDistSweepDialog(false)}>Отмена</Button>
            <Button variant="contained" color="warning" onClick={handleDistSweep} disabled={distSweepLoading || !distSweepTarget || distSweepCurrencies.length === 0}>
              Начать сбор {distSweepCurrencies.map(c => c.toUpperCase()).join(' + ')}
            </Button>
          </>) : (<>
            {sweepProgress.status === 'running' && (
              <Button variant="contained" color="error" onClick={handleSweepAbort}>Остановить</Button>
            )}
            <Button onClick={() => { setDistSweepDialog(false); setSweepProgress(null); setSweepId(null); }} disabled={sweepProgress.status === 'running'}>
              Закрыть
            </Button>
          </>)}
        </DialogActions>
      </Dialog>

      {/* Диалог добавления/редактирования правила кошелька */}
      <Dialog
        open={walletDialog.open}
        onClose={() => setWalletDialog({ open: false, mode: 'add', data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {walletDialog.mode === 'add' ? 'Добавить правило' : 'Редактировать правило'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Адрес кошелька"
            value={walletDialog.data?.wallet_address || ''}
            onChange={(e) => setWalletDialog({
              ...walletDialog,
              data: { ...walletDialog.data, wallet_address: e.target.value }
            })}
            sx={{ mt: 2, mb: 2 }}
            disabled={walletDialog.mode === 'edit'}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Тип правила</InputLabel>
            <Select
              value={walletDialog.data?.rule_type || 'custom'}
              onChange={(e) => setWalletDialog({
                ...walletDialog,
                data: {
                  ...walletDialog.data,
                  rule_type: e.target.value,
                  sell_percentage: e.target.value === 'flipper' ? 100 : e.target.value === 'whitelist' ? 0 : walletDialog.data?.sell_percentage
                }
              })}
              label="Тип правила"
            >
              <MenuItem value="flipper">Флиппер (гашение 100%)</MenuItem>
              <MenuItem value="whitelist">Белый список (не трогать)</MenuItem>
              <MenuItem value="custom">Кастом (указать %)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Процент гашения"
            type="number"
            value={walletDialog.data?.sell_percentage || 100}
            onChange={(e) => setWalletDialog({
              ...walletDialog,
              data: { ...walletDialog.data, sell_percentage: parseInt(e.target.value) }
            })}
            sx={{ mb: 2 }}
            disabled={walletDialog.data?.rule_type === 'whitelist'}
            inputProps={{ min: 0, max: 100 }}
          />

          <TextField
            fullWidth
            label="Комментарий"
            value={walletDialog.data?.comment || ''}
            onChange={(e) => setWalletDialog({
              ...walletDialog,
              data: { ...walletDialog.data, comment: e.target.value }
            })}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWalletDialog({ open: false, mode: 'add', data: null })}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveWalletRule}
            disabled={saving || !walletDialog.data?.wallet_address}
          >
            {saving ? <CircularProgress size={20} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketMaker;
